import { useState } from "react";
import { toast } from "react-toastify";
import { FiSend } from "react-icons/fi";
import { sendReminders } from "../../services/reminderApi";
import { useRole } from "../../context/RoleContext";
import { useCycle } from "../../context/CycleContext";
import Button from "./Button";
import Modal from "./Modal";
import FormField from "./FormField";
import { getErrorMessage } from "../../utils/errors";
import { can, ORG_SCOPES } from "../../services/core";

const api = { sendReminders };

const CHANNELS = ["In-app", "Email"];

export default function RemindButton({ recipients = [], taskType, defaultMessage = "", label = "Remind", size = "sm", variant = "secondary" }) {
  const { profile } = useRole();
  const { cycleId } = useCycle();
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState(CHANNELS);
  const [message, setMessage] = useState(defaultMessage);
  const [busy, setBusy] = useState(false);

  if (!recipients.length || !can(profile, "reminder.manage", ORG_SCOPES)) return null;

  const who = recipients.length === 1 ? recipients[0].name : `${recipients.length} people`;

  const toggle = (channel) =>
    setChannels((list) => (list.includes(channel) ? list.filter((c) => c !== channel) : [...list, channel]));

  const send = async () => {
    if (!channels.length) { toast.error("Choose at least one channel."); return; }
    if (!message.trim()) { toast.error("Write a short message."); return; }
    setBusy(true);
    try {
      await api.sendReminders({ employeeIds: recipients.map((r) => r.id), cycleId, recipients, taskType, channels, message: message.trim(), sentByName: profile?.name });
      toast.success(`Reminder sent to ${who}.`);
      setOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span onClick={(e) => e.stopPropagation()} role="presentation" style={{ display: "inline-flex" }}>
      <Button
        size={size}
        variant={variant}
        icon={FiSend}
        onClick={() => {
          setMessage(defaultMessage);
          setOpen(true);
        }}
      >
        {label}
      </Button>
      <Modal
        open={open}
        size="sm"
        title={`Remind ${who}`}
        subtitle={taskType}
        onClose={busy ? undefined : () => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button icon={FiSend} loading={busy} onClick={send}>Send reminder</Button>
          </>
        }
      >
        <div className="form-stack">
          <FormField label="Send by">
            <div className="segmented" role="group" aria-label="Channels">
              {CHANNELS.map((c) => (
                <button key={c} type="button" aria-pressed={channels.includes(c)} className={channels.includes(c) ? "is-active" : ""} onClick={() => toggle(c)}>
                  {c}
                </button>
              ))}
            </div>
          </FormField>
          <FormField label="Message" required>
            <textarea rows={3} value={message} maxLength={300} onChange={(e) => setMessage(e.target.value)} />
          </FormField>
        </div>
      </Modal>
    </span>
  );
}
